import React, { useEffect, useState } from "react";
import Breadcrumbs from "../../common/Breadcrumbs";
import { useRole } from "../../context/RoleContext";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import { Box, Button, Divider, Typography } from "@mui/material";
import {
  MainContainer,
  paytextStyle,
  deviderStyle,
  headingStyle,
  inputLabelStyle,
  uploadButtonContainer,
  lableAndValueStyle,
  greyGridStyle,
  uploadPaymentStyle,
  transactionInputStyle,
  makePaymentButtonStyle,
  uploadDocumentStyle,
  fileUploadStyle,
  headingTextStyle,
  noFileSelectedStyles,
  uploadSucessImgStyles,
  uploadedFileContainerTextStyles,
  paymentDetailsBoxStyle,
  backIconStyle,
} from "./PaymentStyle";
import {
  breakdownRowStyle,
  breakdownTotalStyle,
} from "../orderTracking/OrderTrackingStyles";
import { BankDetailsType } from "../../constants/types";
import { bankDetails as defaultBankDetails } from "../../constants/bankDetail";
import { OrderDetails as OrderDetailsProps } from "../../dto/orderSummaryTypes";
import crossIcon from "../../assets/crossIcon.png.png";
import { updateOrderPaymentService } from "../../service/orderPaymentService";
import Toast from "../../common/Toast";
import addFileIcon from "../../assets/icons/file-add.svg";
import greenTick from "../../assets/icons/green-tick-upload.svg";
import backIcon from "../../assets/arrow-left.svg";
import popupSucessIcon from "../../assets/icons/payment-success-popup-icon.svg";
import PaymentDialog from "../../common/paymentDialog";
import { fetchOrderDetails } from "../../api/orderFinalAPI";
import { getPresignedUrls, uploadFileToS3Api } from "../../api/customerAPI";
import bucketMapping from "../../constants/S3BuketMapping";

const Payment = () => {
  const [FileData, setFileData] = useState<{ file?: File }>({});
  const [transactionID, setTransactionID] = useState("");
  const [orderDetails, setOrderDetails] = useState<OrderDetailsProps>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { role } = useRole();
  const { id: orderId } = useParams<{ id: string }>();
  const [error, setError] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const navigate = useNavigate();
  const orgId = localStorage.getItem("orgId");

  useEffect(() => {
    const getOrderDetails = async () => {
      if (!orderId) return;
      try {
        const data = await fetchOrderDetails(orderId);
        if (data?.data) {
          setOrderDetails(data.data);
        }
      } catch (error) {
        console.error("Failed to fetch order details", error);
      }
    };
    getOrderDetails();
  }, [orderId]);

  const isSellerPortal = () => {
    if (
      role === "maltora-seller" ||
      location.pathname.includes("/maltora-seller")
    ) {
      return true;
    }
    return false;
  };

  const getLinks = () => {
    if (isSellerPortal()) {
      return ["/maltora-seller/orders", "#"];
    }
    return [
      "/maltora-customer/home",
      "/maltora-customer/my-orders",
      `/maltora-customer/order-tracking/${orderId}`,
      "#",
    ];
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setFileData({ file });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTransactionID(e.target.value);
  };

  const handlePayment = async () => {
    if (!orderId) return;

    try {
      if (FileData?.file) {
        const presigned = await getPresignedUrls(
          [
            {
              fileName: FileData.file.name,
              fileType: FileData.file.type,
            },
          ],
          bucketMapping.customer
        );

        const presignedData = presigned?.[0];
        if (!presignedData) throw new Error("Failed to get presigned URL");

        const uploadSuccess = await uploadFileToS3Api(
          presignedData.presignedUrl,
          FileData.file,
          FileData.file.type
        );

        if (!uploadSuccess) throw new Error("S3 upload failed");

        await updateOrderPaymentService(
          {
            uploadedDocuments: [
              {
                key: presignedData.key,
                fileName: FileData.file.name,
                fileType: FileData.file.type,
              },
            ],
            transactionId: transactionID || undefined,
            newPayment: true,
          },
          orderId
        );
      } else if (transactionID) {
        await updateOrderPaymentService(
          {
            uploadedDocuments: [],
            transactionId: transactionID,
            newPayment: true,
          },
          orderId
        );
      }

      setDialogOpen(true);
    } catch (error) {
      console.log(error);
      setToastMessage("Failed To update payment");
      setError(true);
      setShowToast(true);
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const backToShopping = () => {
    handleClose();
    navigate(`/maltora-customer/products`);
  };

  const redirectToOrderSummary = () => {
    handleClose();
    navigate(`/maltora-customer/order-tracking/${orderId}`);
  };

  const handleRemoveFile = () => {
    setFileData({});
  };

  const handleBackClick = () => {
    window.history.back();
  };

  const getPopupContent = () => {
    return (
      <>
        Thank’s for your order Maltora. Your order will be processed as soon as
        possible.
        <br />
        Make sure you make note of your order number, which is{" "}
        <span id="OrdId">{orderId ?? ""}</span>. <br />
        You will be receiving an email shortly with an invoice from your order.
      </>
    );
  };

  // Total calculation for payment button
  const total = orderDetails?.payment?.exciseBreakdown
    ? orderDetails.payment.exciseBreakdown.grandTotal +
      (orderDetails.payment.serviceTotal || 0)
    : (orderDetails?.payment?.exciseTotal || 0) +
      (orderDetails?.payment?.serviceTotal || 0);
  const formattedTotal = total.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
  });

  // Dynamic bank details from API response
  const bankDetails = [
    {
      label: "Bank Name",
      value: orderDetails?.sellerBankDetails?.bankName || "",
    },
    {
      label: "Bank A/c Name",
      value: orderDetails?.sellerBankDetails?.bankAccountName || "",
    },
    {
      label: "Bank A/c No",
      value: orderDetails?.sellerBankDetails?.bankAccountNumber || "",
    },
    {
      label: "Bank Branch",
      value: orderDetails?.sellerBankDetails?.bankBranch || "",
    },
    {
      label: "Bank IFSC Code",
      value: orderDetails?.sellerBankDetails?.ifscCode || "",
    },
  ];

  return (
    <>
      {isSellerPortal() && (
        <Breadcrumbs
          paths={["All Orders", "Edit Order Status"]}
          links={getLinks()}
        />
      )}
      <Box sx={paymentDetailsBoxStyle}>
        <Typography sx={{ ...headingTextStyle }}>Payment Details</Typography>
        <Typography sx={{ ...paytextStyle }}>
          <Box
            component="img"
            alt="back-icon"
            src={backIcon}
            sx={backIconStyle}
            onClick={handleBackClick}
          />
        </Typography>
      </Box>
      <MainContainer>
        {" "}
        <Grid container spacing={1}>
          <Grid
            size={{ xs: 12, sm: 6, md: 7 }}
            sx={{ marginTop: "14px", mb: "14px" }}
          >
            <Typography
              sx={{ ...paytextStyle, marginTop: "12px", fontSize: "24px" }}
            >
              Make the payment to the bank details provided below
            </Typography>
            <Grid container spacing={[0, 2]}>
              {bankDetails.map((detail: BankDetailsType, index) => (
                <Grid key={index} size={{ xs: 12, sm: 6, md: 6 }}>
                  <Box sx={lableAndValueStyle}>
                    <Typography sx={headingStyle}>{detail.label}</Typography>
                    <Typography sx={inputLabelStyle}>{detail.value}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 5 }} sx={greyGridStyle}>
            {" "}
            <Typography sx={paytextStyle}>
              After completing your payment, please either upload a receipt or
              screenshot with the transaction details or provide the Transaction
              ID below.
            </Typography>
            <Typography mt={"10px"} sx={uploadPaymentStyle}>
              Upload a payment receipt or screenshot here
            </Typography>
            <Typography
              mt={"20px"}
              sx={{ ...uploadPaymentStyle, fontWeight: 500 }}
            >
              Upload Documents
            </Typography>
            <label
              htmlFor="UploadButton"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Button
                variant="outlined"
                component="span"
                style={uploadButtonContainer}
              >
                <Box
                  component="img"
                  src={addFileIcon}
                  alt="upload"
                  sx={{ marginRight: "5px" }}
                />
                <Typography sx={{ ...uploadPaymentStyle, fontSize: "10px" }}>
                  Click to upload
                </Typography>
              </Button>
              {!FileData.file && (
                <span style={noFileSelectedStyles}>No file chosen</span>
              )}
            </label>
            <input
              type="file"
              id="UploadButton"
              data-testid="fileUpload"
              onChange={handleFileUpload}
              style={fileUploadStyle}
            />
            {/* Show uploaded file name below */}
            {FileData.file && (
              <Box mt={3} sx={uploadDocumentStyle}>
                <Box
                  component="img"
                  alt="upload-success"
                  src={greenTick}
                  sx={uploadSucessImgStyles}
                />
                <Box sx={{ padding: "5px 0" }}>
                  <Typography
                    sx={{
                      ...uploadedFileContainerTextStyles.fileName,
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {FileData.file.name}
                  </Typography>
                  <Typography sx={uploadedFileContainerTextStyles.uploadedText}>
                    Upload complete
                  </Typography>
                </Box>
                <Box
                  component="img"
                  onClick={handleRemoveFile}
                  alt="remove file"
                  src={crossIcon}
                  sx={{
                    cursor: "pointer",
                    marginLeft: "40px",
                    marginRight: "20px",
                  }}
                />
              </Box>
            )}
            <Divider style={deviderStyle}>
              <strong>or</strong>
            </Divider>
            <Typography sx={uploadPaymentStyle}>Transaction ID</Typography>
            <input
              type="text"
              id="transaction-id"
              style={transactionInputStyle}
              data-testid="transaction-id"
              onChange={handleInputChange}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              size="large"
              variant="contained"
              sx={makePaymentButtonStyle}
              onClick={handlePayment}
              disabled={!FileData.file && !transactionID}
            >
              {!FileData.file && !transactionID
                ? `Pay ${formattedTotal}`
                : `Paid ${formattedTotal}`}
            </Button>
          </Grid>
        </Grid>
      </MainContainer>

      <PaymentDialog
        open={dialogOpen}
        title="Payment Confirmed"
        message={getPopupContent()}
        secondaryText="Back to shopping"
        primaryText="Track your order"
        secondaryAction={() => backToShopping()}
        primaryAction={() => redirectToOrderSummary()}
        imageUrl={popupSucessIcon}
      />

      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />
    </>
  );
};

export default Payment;
